import React, { useState } from 'react';
import { Box, Typography, Container, Button, Card, CardContent, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import AuthModal from '../components/auth/AuthModal';
import {
  Speed,
  Security,
  CheckCircle,
  Gavel,
  Description,
  Phone,
  AutoAwesome,
  TrendingUp,
  BusinessCenter,
  Home
} from '@mui/icons-material';
import judgeIcon from '../assets/icons/iconsax-judge.svg';
import { PAY_PER_USE_PRICING, CREDIT_VALUE_CZK } from '../config/pricing';

const ConsultationInfoPage: React.FC = () => {
  const navigate = useNavigate();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'login' | 'register'>('login');
  const consultationPricing = PAY_PER_USE_PRICING.find(item => item.key === 'consultation');
  const formatCurrency = (value: number) => value.toLocaleString('cs-CZ');
  const creditValue = CREDIT_VALUE_CZK.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const features = [
    {
      icon: <Speed sx={{ fontSize: 32 }} />,
      title: 'Rychlé odpovědi',
      description: 'Odpověď za 30 sekund místo týdnů čekání na termín'
    },
    {
      icon: <Security sx={{ fontSize: 32 }} />,
      title: 'Bezpečné prostředí',
      description: 'Bankovní úroveň zabezpečení vašich dat'
    },
    {
      icon: <CheckCircle sx={{ fontSize: 32 }} />,
      title: 'Ověřené informace',
      description: '98% přesnost ověřená českými právníky'
    },
    {
      icon: <Gavel sx={{ fontSize: 32 }} />,
      title: 'České právo',
      description: 'Specializace na české prostředí a lokální judikáty'
    }
  ];

  const useCases = [
    {
      title: 'Pracovní právo',
      icon: <BusinessCenter sx={{ fontSize: 32 }} />,
      description: 'Otázky k pracovním smlouvám, výpovědím, náhradám škod nebo mobingu na pracovišti.',
      examples: ['Může mi zaměstnavatel dát výpověď?', 'Jaké mám nároky při skončení pracovního poměru?']
    },
    {
      title: 'Bydlení a nemovitosti',
      icon: <Home sx={{ fontSize: 32 }} />,
      description: 'Problémy s nájmem, kaucí, reklamacemi nebo sousedskými spory.',
      examples: ['Majitel mi nechce vrátit kauci', 'Soused mi překáží v užívání bytu']
    },
    {
      title: 'Rodinné právo',
      icon: <AutoAwesome sx={{ fontSize: 32 }} />,
      description: 'Rozvod, alimenty, péče o děti nebo majetková práva manželů.',
      examples: ['Jak probíhá rozvod?', 'Kolik platím na výživném?']
    },
    {
      title: 'Spotřebitelské právo',
      icon: <Description sx={{ fontSize: 32 }} />,
      description: 'Reklamace výrobků, vrácení zboží, smlouvy nebo podvodné praktiky.',
      examples: ['Jak reklamovat vadný výrobek?', 'Můžu odstoupit od smlouvy?']
    }
  ];

  const process = [
    {
      step: '1',
      title: 'Popište situaci',
      description: 'Napište nám svůj právní problém vlastními slovy. Nemusíte znát právní terminologii.'
    },
    {
      step: '2',
      title: 'Upřesníme detaily',
      description: 'Systém vám položí několik doplňujících otázek pro lepší pochopení situace.'
    },
    {
      step: '3',
      title: 'Získáte odpověď',
      description: 'Během několika minut obdržíte právní analýzu s doporučeními podle českého práva.'
    },
  ];

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100vh',
      backgroundColor: '#F2F2F2',
      position: 'relative'
    }}>
      <Container maxWidth={false} sx={{ 
        maxWidth: '1920px',
        px: { xs: 2, sm: 3, md: 4, lg: '277px' }
      }}>
        {/* Hero Section */}
        <Box sx={{
          position: 'relative',
          mt: { xs: '100px', sm: '120px', md: '132px' },
          mb: { xs: 8, sm: 10, md: 12 }
        }}>
          {/* Enhanced Gradient Background */}
          <Box sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '400%', sm: '250%', md: '180%', lg: '1600px' },
            height: { xs: '400px', sm: '450px', md: '500px' },
            borderRadius: '50%',
            background: 'radial-gradient(circle at 50% 50%, rgba(100, 100, 255, 0.1) 0%, rgba(255, 100, 100, 0.05) 30%, rgba(128, 128, 128, 0.3) 60%, rgba(237, 237, 237, 0) 100%)',
            filter: 'blur(120px)',
            zIndex: 0
          }} />

          <Box sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: { xs: 3, sm: 4, md: 5 },
            py: { xs: 6, sm: 7, md: 8 },
            zIndex: 1
          }}>
            <Box 
              component="img" 
              src={judgeIcon} 
              alt="Právní konzultace" 
              sx={{ 
                width: { xs: '80px', sm: '100px', md: '120px' },
                height: { xs: '80px', sm: '100px', md: '120px' },
                filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.1))'
              }} 
            />
            
            <Typography sx={{ 
              fontFamily: 'Inter',
              fontWeight: 800,
              fontSize: { xs: '32px', sm: '42px', md: '52px', lg: '64px' },
              lineHeight: { xs: 1.1, md: 1.1 },
              textAlign: 'center',
              color: '#000000',
              maxWidth: { xs: '100%', sm: '700px', md: '900px', lg: '1000px' },
              background: 'linear-gradient(135deg, #1F1F1F 0%, #666666 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              px: { xs: 2, sm: 3 }
            }}>
              Právní poradenství bez vysokých{' '}
              <Box component="span" sx={{ 
                background: 'linear-gradient(135deg, #e72f0d 0%, #e72f0d 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                hodinových sazeb
              </Box>
            </Typography>

            <Typography sx={{ 
              fontFamily: 'Inter',
              fontWeight: 400,
              fontSize: { xs: '16px', sm: '18px', md: '20px' },
              lineHeight: 1.5,
              textAlign: 'center',
              color: '#666666',
              maxWidth: '700px',
              px: { xs: 2, sm: 3 }
            }}>
              Okamžité právní odpovědi za zlomek ceny tradičního poradenství.
            </Typography>

            {consultationPricing && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
                backgroundColor: 'rgba(255,255,255,0.85)',
                borderRadius: '999px',
                px: { xs: 2, sm: 3 },
                py: 1,
                boxShadow: '0 8px 18px rgba(0,0,0,0.08)'
              }}>
                <Chip
                  label={`${formatCurrency(consultationPricing.priceCZK)} Kč`}
                  sx={{
                    backgroundColor: '#1F1F1F',
                    color: '#FFFFFF',
                    fontWeight: 600
                  }}
                />
                <Chip
                  label={`${formatCurrency(consultationPricing.credits)} kreditů`}
                  sx={{
                    backgroundColor: 'rgba(231,47,13,0.12)',
                    color: '#e72f0d',
                    fontWeight: 600
                  }}
                />
                <Typography sx={{ fontFamily: 'Inter', fontSize: '13px', color: '#424242', fontWeight: 500 }}>
                  1 kredit = {creditValue} Kč. Platba se odečte až po doručení odpovědi.
                </Typography>
              </Box>
            )}
            
            <Box sx={{
              display: 'flex',
              gap: { xs: 2, sm: 3 },
              flexWrap: 'wrap',
              justifyContent: 'center',
              mt: 2
            }}>
              <Button
                onClick={() => {
                  const intended = '/consultation';
                  try { sessionStorage.setItem('intendedPath', intended); } catch {}
                  navigate(intended);
                }}
                size="large"
                sx={{
                  padding: { xs: '14px 28px', sm: '16px 32px' },
                  bgcolor: '#1F1F1F !important',
                  backgroundColor: '#1F1F1F !important',
                  background: '#1F1F1F !important',
                  color: '#FFFFFF !important',
                  fontSize: { xs: '14px', sm: '16px' },
                  fontWeight: 600,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  border: 'none !important',
                  boxShadow: '0 4px 12px rgba(31, 31, 31, 0.15)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: '#e72f0d !important',
                    backgroundColor: '#e72f0d !important',
                    background: '#e72f0d !important',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 20px rgba(231, 47, 13, 0.25)'
                  }
                }}
              >
                Začít konzultaci
              </Button>
              {/* Removed back-to-home button as requested */}
            </Box>

            {/* Jednoduchý návod – konzultace */}
            <Box sx={{
              mt: { xs: 3, sm: 4 },
              p: { xs: 2.5, sm: 3 },
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.06)',
              border: '1px solid rgba(0,0,0,0.06)',
              maxWidth: 900,
              mx: 'auto'
            }}>
              <Typography sx={{ fontFamily: 'Inter', fontWeight: 700, fontSize: { xs: '20px', sm: '22px' }, color: '#1F1F1F', textAlign: 'center', mb: 1.5 }}>
                Jak začít.
              </Typography>
              <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: { xs: '14px', sm: '15px' }, color: '#666', textAlign: 'center', mb: 2.5 }}>
                Po kliknutí na „Začít konzultaci“ budete přesměrováni na Dashboard. Tam proběhne přihlášení/registrace a odkud ovládáte všechny funkce.
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <CheckCircle sx={{ color: '#10b981', mt: '2px' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 600, color: '#1F1F1F', fontSize: { xs: '15px', sm: '16px' } }}>1) Otevřete konzultaci</Typography>
                    <Typography sx={{ color: '#555', fontSize: { xs: '14px', sm: '15px' } }}>Klikněte na „Začít konzultaci“. V Dashboardu se přihlaste/zaregistrujte.</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <CheckCircle sx={{ color: '#10b981', mt: '2px' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 600, color: '#1F1F1F', fontSize: { xs: '15px', sm: '16px' } }}>2) Napište dotaz</Typography>
                    <Typography sx={{ color: '#555', fontSize: { xs: '14px', sm: '15px' } }}>Stručně popište situaci. Systém vám položí pár doplňujících otázek.</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <CheckCircle sx={{ color: '#10b981', mt: '2px' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 600, color: '#1F1F1F', fontSize: { xs: '15px', sm: '16px' } }}>3) Získejte odpověď</Typography>
                    <Typography sx={{ color: '#555', fontSize: { xs: '14px', sm: '15px' } }}>Dostanete srozumitelnou odpověď s odkazy na zákony a praktickými kroky.</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <CheckCircle sx={{ color: '#10b981', mt: '2px' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 600, color: '#1F1F1F', fontSize: { xs: '15px', sm: '16px' } }}>4) Vše najdete v Dashboardu</Typography>
                    <Typography sx={{ color: '#555', fontSize: { xs: '14px', sm: '15px' } }}>Historie konzultací je uložená. Můžete se kdykoliv vrátit.</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Features Section */}
        <Box sx={{
          position: 'relative',
          mb: { xs: 8, sm: 10, md: 12 }
        }}>
          <Typography sx={{
            fontFamily: 'Inter',
            fontWeight: 700,
            fontSize: { xs: '28px', sm: '36px', md: '42px' },
            lineHeight: 1.2,
            textAlign: 'center',
            color: '#1F1F1F',
            mb: { xs: 4, sm: 6 },
            maxWidth: '800px',
            mx: 'auto'
          }}>
            Proč zvolit AI právní konzultace?
          </Typography>
          
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: { xs: 3, sm: 4 },
            mb: { xs: 6, sm: 8 }
          }}>
            {features.map((feature, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  padding: { xs: '24px', sm: '32px' },
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '20px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  transition: 'all 0.5s ease-out',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
                  }
                }}
              >
                <Box sx={{ 
                  color: '#1F1F1F',
                  mb: 1
                }}>
                  {feature.icon}
                </Box>
                <Typography sx={{
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontSize: { xs: '18px', sm: '20px' },
                  color: '#1F1F1F',
                  mb: 1
                }}>
                  {feature.title}
                </Typography>
                <Typography sx={{
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontSize: { xs: '14px', sm: '15px' },
                  lineHeight: 1.6,
                  color: '#666666'
                }}>
                  {feature.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* How it works */}
        <Box sx={{
          position: 'relative',
          mb: { xs: 8, sm: 10, md: 12 }
        }}>
          <Typography sx={{
            fontFamily: 'Inter',
            fontWeight: 700,
            fontSize: { xs: '28px', sm: '36px', md: '42px' },
            lineHeight: 1.2,
            textAlign: 'center',
            color: '#1F1F1F',
            mb: { xs: 4, sm: 6 }
          }}>
            Jak funguje konzultace?
          </Typography>
          
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            gap: { xs: 3, sm: 4 },
            mb: { xs: 4, sm: 6 },
            justifyItems: 'center'
          }}>
            {process.map((item, index) => (
              <Card
                key={index}
                sx={{
                  position: 'relative',
                  width: { xs: '100%', sm: '280px', lg: '320px' },
                  maxWidth: '320px',
                  padding: { xs: '24px', sm: '28px' },
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '20px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  overflow: 'visible',
                  transition: 'all 0.5s ease-out',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
                  }
                }}
              >
                <Box sx={{
                  position: 'absolute',
                  top: -15,
                  left: 24,
                  width: 30,
                  height: 30,
                  backgroundColor: '#1F1F1F',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10
                }}>
                  <Typography sx={{
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '14px'
                  }}>
                    {item.step}
                  </Typography>
                </Box>
                
                <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                  <Typography sx={{
                    fontFamily: 'Inter',
                    fontWeight: 600,
                    fontSize: { xs: '16px', sm: '18px' },
                    color: '#1F1F1F',
                    mb: 1,
                    mt: 1
                  }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    fontSize: { xs: '14px', sm: '15px' },
                    lineHeight: 1.5,
                    color: '#666666'
                  }}>
                    {item.description}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        {/* Use Cases */}
        <Box sx={{
          position: 'relative',
          mb: { xs: 8, sm: 10, md: 12 }
        }}>
          <Typography sx={{
            fontFamily: 'Inter',
            fontWeight: 700,
            fontSize: { xs: '28px', sm: '36px', md: '42px' },
            lineHeight: 1.2,
            textAlign: 'center',
            color: '#1F1F1F',
            mb: { xs: 4, sm: 6 }
          }}>
            S čím vám můžeme pomoci?
          </Typography>
          
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: { xs: 3, sm: 4 }
          }}>
            {useCases.map((useCase, index) => (
              <Box
                key={index}
                sx={{
                  padding: { xs: '24px', sm: '32px' },
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '20px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  transition: 'all 0.5s ease-out',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
                  }
                }}
              >
                <Box sx={{ 
                  color: '#1F1F1F',
                  mb: 2
                }}>
                  {useCase.icon}
                </Box>
                <Typography sx={{
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontSize: { xs: '18px', sm: '20px' },
                  color: '#1F1F1F',
                  mb: 2
                }}>
                  {useCase.title}
                </Typography>
                <Typography sx={{
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontSize: { xs: '14px', sm: '15px' },
                  lineHeight: 1.6,
                  color: '#666666',
                  mb: 3
                }}>
                  {useCase.description}
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{
                    fontFamily: 'Inter',
                    fontWeight: 500,
                    fontSize: '13px',
                    color: '#1F1F1F',
                    mb: 1
                  }}>
                    Příklady otázek:
                  </Typography>
                  {useCase.examples.map((example, idx) => (
                    <Typography key={idx} sx={{
                      fontFamily: 'Inter',
                      fontWeight: 400,
                      fontSize: '12px',
                      color: '#666666',
                      fontStyle: 'italic',
                      mb: 0.5,
                      '&:before': {
                        content: '"• "',
                        color: '#1F1F1F'
                      }
                    }}>
                      {example}
                    </Typography>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Important Notice */}
        <Box sx={{
          position: 'relative',
          mb: { xs: 6, sm: 8, md: 10 }
        }}>
          <Box sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
            padding: { xs: '16px', sm: '20px' },
            backgroundColor: '#4C4C4C',
            borderRadius: '12px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
            zIndex: 1
          }}>
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 500,
              fontSize: { xs: '12px', sm: '14px' },
              lineHeight: 1.5,
              textAlign: 'center',
              color: '#DCDCDC'
            }}>
              <strong>Důležité upozornění:</strong> Tento systém poskytuje obecné právní informace a AI analýzu. Pro konkrétní právní záležitosti vždy konzultujte kvalifikovaného advokáta.
            </Typography>
          </Box>
        </Box>

        {/* Final CTA */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: { xs: 4, sm: 5 },
          padding: { xs: '48px 32px', sm: '64px 40px' },
          background: 'linear-gradient(135deg, #1F1F1F 0%, #333333 100%)',
          borderRadius: '24px',
          boxShadow: '0px 20px 60px rgba(0, 0, 0, 0.25)',
          mb: { xs: 6, sm: 8, md: 10 }
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 700,
              fontSize: { xs: '28px', sm: '36px', md: '42px' },
              lineHeight: 1.1,
              color: '#FFFFFF',
              mb: 3
            }}>
              Připraveni ušetřit na advokátních honorářích?
            </Typography>
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 400,
              fontSize: { xs: '16px', sm: '18px' },
              lineHeight: 1.5,
              color: '#B0B0B0',
              maxWidth: '600px'
            }}>
              Registrace je zdarma – konzultace stojí 200 kreditů (50 Kč) a zaplatíte je až po doručení odpovědi.
            </Typography>
          </Box>
          
          <Button
            onClick={() => {
              const intended = '/consultation';
              try { sessionStorage.setItem('intendedPath', intended); } catch {}
              navigate(intended);
            }}
            size="large"
            sx={{
              padding: { xs: '16px 32px', sm: '20px 40px' },
              backgroundColor: '#FFFFFF',
              color: '#1F1F1F',
              borderRadius: '12px',
              fontSize: { xs: '16px', sm: '18px' },
              fontWeight: 700,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              boxShadow: '0 8px 32px rgba(255,255,255,0.2)',
              '&:hover': {
                backgroundColor: '#F0F0F0',
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 40px rgba(255,255,255,0.3)'
              }
            }}
          >
            Začít konzultaci (200 kreditů)
          </Button>

          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            opacity: 0.8,
            mt: 2
          }}>
            <Typography sx={{ 
              fontSize: '14px', 
              color: '#B0B0B0',
              fontWeight: 500,
              textAlign: 'center'
            }}>
              • Registrace zdarma  • 1 kredit = {creditValue} Kč  • Kredity lze přenést do dalšího měsíce
            </Typography>
          </Box>
        </Box>
      </Container>

      <Footer />

      {/* Auth Modal */}
      <AuthModal
        open={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        initialMode={authModalMode}
      />
    </Box>
  );
};

export default ConsultationInfoPage;
